import { Head, router } from "@inertiajs/react"
import { ExternalLink } from "lucide-react"
import { toast } from "sonner"

import { type DayHours } from "@/components/accounts/account-form"
import AccountIntegrationsDisplay from "@/components/accounts/account-integrations-display"
import { AccountStateBadge } from "@/components/accounts/account-state-badge"
import {
  type AccountStandardAgent,
  AccountTrialTable,
} from "@/components/admin/account-trial-table"
import {
  type HoursStatus,
  hoursSummary,
} from "@/components/business-profile/hours-editor"
import TextLink from "@/components/text-link"
import AttributeDisplay from "@/components/ui/attribute-display"
import AttributesContainer from "@/components/ui/attributes-container"
import { Badge } from "@/components/ui/badge"
import LogoPreview from "@/components/ui/logo-preview"
import RecordPageHeader from "@/components/ui/record-page-header"
import type { RecordPageHeaderAction } from "@/components/ui/record-page-header"
import AdminLayout from "@/layouts/admin-layout"
import {
  adminAccountPath,
  adminAccountsPath,
  adminAgentPath,
  editAdminAccountPath,
  generateLandingPageAdminAccountPath,
  regenerateDemoDataAdminAccountPath,
  resetOnboardingAdminAccountPath,
} from "@/routes"
import type {
  Account,
  AccountIntegrationDisplay,
  BreadcrumbItem,
} from "@/types"

interface ProvisionalReceptionist {
  uid: string
  state: string
  retell_agent_id: string | null
}

interface BusinessProfileView {
  business_name: string | null
  phone: string | null
  address: string | null
  website: string | null
  description: string | null
  services: string | null
  business_category: string | null
  hours: DayHours[] | null
  hours_status: HoursStatus | null
}

// Show a dash for empty business-profile fields rather than a blank row.
function bpValue(value: string | null | undefined): string {
  return value?.trim() ? value : "—"
}

interface LandingPageVisitView {
  id: number
  created_at: string
  ip_address: string | null
  location: string
  ctas: Record<string, number>
}

interface LandingPageAnalytics {
  visit_count: number
  cta_counts: Record<string, number>
  recent_visits: LandingPageVisitView[]
}

// IMP-331: human labels for the tracked CTAs.
const CTA_LABELS: Record<string, string> = {
  activate: "Activate",
  test_call: "Start test call",
  contact_us: "Contact us",
}

export default function ShowAccount({
  account,
  account_integrations,
  landing_page_analytics,
  standard_agents,
}: {
  account: Account & {
    landing_page_url?: string | null
    landing_page_heading_override?: string | null
    landing_page_subheading_override?: string | null
    provisional_receptionist?: ProvisionalReceptionist | null
    business_profile?: BusinessProfileView | null
    // IMP-411: the account's hosted, carrier-compliant SMS policy URLs (the
    // business no longer sees these on the registration form; the admin submits
    // them to the carrier).
    sms_legal?: {
      privacy_policy_url: string
      sms_terms_url: string
    } | null
  }
  account_integrations: AccountIntegrationDisplay[]
  landing_page_analytics: LandingPageAnalytics
  // IMP-394: null for partner-managed accounts (no trials there).
  standard_agents: AccountStandardAgent[] | null
}) {
  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Accounts",
      href: adminAccountsPath(),
    },
    {
      title: account.name,
      href: adminAccountPath({ id: account.uid }),
    },
  ]

  const actions: RecordPageHeaderAction[] = []

  actions.push({
    label: account.provisional_receptionist
      ? "Regenerate landing page"
      : "Generate landing page",
    onClick: () => {
      router.post(
        generateLandingPageAdminAccountPath({ id: account.uid }),
        {},
        {
          onError: () => toast.error("Failed to queue landing page generation"),
        },
      )
    },
  })

  if (account.demo_mode) {
    actions.push({
      label: "Regenerate Demo Data",
      onClick: () => {
        router.post(
          regenerateDemoDataAdminAccountPath({ id: account.uid }),
          {},
          {
            onError: () => toast.error("Failed to regenerate demo data"),
          },
        )
      },
    })
    actions.push({
      label: "Reset to Onboarding",
      onClick: () => {
        if (
          !confirm(
            "Wipe this account's agents and business profile, and send it back to the onboarding flow?",
          )
        ) {
          return
        }
        router.post(
          resetOnboardingAdminAccountPath({ id: account.uid }),
          {},
          {
            onError: () => toast.error("Failed to reset onboarding"),
          },
        )
      },
    })
  }

  return (
    <AdminLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />
      <div className="mx-auto flex h-full w-full max-w-3xl flex-col gap-6 p-4">
        <RecordPageHeader
          title={account.name}
          editPath={editAdminAccountPath({ id: account.uid })}
          deletePath={adminAccountPath({ id: account.uid })}
          deleteDisabledReason={
            account.deletable === false
              ? "This account has billing records (subscriptions or invoices) and can't be deleted."
              : undefined
          }
          actions={actions}
          // IMP-403: render the account-state badge (distinct Prospect styling)
          // rather than RecordPageHeader's agent-oriented default.
          badges={
            <>
              <AccountStateBadge state={account.state} />
              {account.demo_mode && <Badge variant="secondary">Demo</Badge>}
            </>
          }
        />
        <div className="space-y-3">
          <h2 className="text-base font-semibold">Details</h2>
          <AttributesContainer className="gap-6">
            <AttributeDisplay
              label="Logo"
              value={
                account.logo_url ? (
                  <LogoPreview src={account.logo_url} alt={account.name} />
                ) : (
                  "—"
                )
              }
            />
          </AttributesContainer>
        </div>

        {(Boolean(account.landing_page_url) ||
          Boolean(account.landing_page_uid) ||
          Boolean(account.provisional_receptionist)) && (
          <div className="space-y-3">
            <h2 className="text-base font-semibold">Landing page</h2>
            <AttributesContainer className="gap-6">
              {account.landing_page_url && (
                <AttributeDisplay
                  label="URL"
                  value={
                    <a
                      href={account.landing_page_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 break-all underline"
                    >
                      {account.landing_page_url}
                      <ExternalLink className="size-3 shrink-0" />
                    </a>
                  }
                />
              )}
              {account.landing_page_uid && (
                <AttributeDisplay
                  label="Heading override"
                  value={bpValue(account.landing_page_heading_override)}
                />
              )}
              {account.landing_page_uid && (
                <AttributeDisplay
                  label="Subheading override"
                  value={bpValue(account.landing_page_subheading_override)}
                />
              )}
              {account.provisional_receptionist && (
                <AttributeDisplay
                  label="Provisional receptionist"
                  value={
                    <TextLink
                      href={adminAgentPath({
                        id: account.provisional_receptionist.uid,
                      })}
                      as="div"
                      prefetch
                    >
                      {account.provisional_receptionist.uid} (
                      {account.provisional_receptionist.state})
                    </TextLink>
                  }
                />
              )}
            </AttributesContainer>
          </div>
        )}

        {account.sms_legal && (
          <div className="space-y-3">
            <h2 className="text-base font-semibold">SMS legal documents</h2>
            <p className="text-muted-foreground text-sm">
              Publicly hosted, account-scoped Privacy Policy and SMS Terms for
              A2P 10DLC registration — submit these URLs to the carrier.
            </p>
            <AttributesContainer className="gap-6">
              <AttributeDisplay
                label="Privacy Policy"
                value={
                  <a
                    href={account.sms_legal.privacy_policy_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 break-all underline"
                  >
                    {account.sms_legal.privacy_policy_url}
                    <ExternalLink className="size-3 shrink-0" />
                  </a>
                }
              />
              <AttributeDisplay
                label="SMS Terms"
                value={
                  <a
                    href={account.sms_legal.sms_terms_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 break-all underline"
                  >
                    {account.sms_legal.sms_terms_url}
                    <ExternalLink className="size-3 shrink-0" />
                  </a>
                }
              />
            </AttributesContainer>
          </div>
        )}

        {account.business_profile && (
          <div className="space-y-3">
            <h2 className="text-base font-semibold">Business profile</h2>
            <AttributesContainer className="gap-6">
              <AttributeDisplay
                label="Business name"
                value={bpValue(account.business_profile.business_name)}
              />
              <AttributeDisplay
                label="Phone"
                value={bpValue(account.business_profile.phone)}
              />
              <AttributeDisplay
                label="Address"
                value={bpValue(account.business_profile.address)}
              />
              <AttributeDisplay
                label="Website"
                value={
                  account.business_profile.website ? (
                    <a
                      href={account.business_profile.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 break-all underline"
                    >
                      {account.business_profile.website}
                      <ExternalLink className="size-3 shrink-0" />
                    </a>
                  ) : (
                    bpValue(null)
                  )
                }
              />
              <AttributeDisplay
                label="Business category"
                value={bpValue(account.business_profile.business_category)}
              />
              <AttributeDisplay
                label="Hours"
                value={
                  account.business_profile.hours_status
                    ? hoursSummary(
                        account.business_profile.hours_status,
                        account.business_profile.hours ?? [],
                      )
                    : bpValue(null)
                }
              />
              <AttributeDisplay
                label="Description"
                value={bpValue(account.business_profile.description)}
              />
              <AttributeDisplay
                label="Services"
                value={bpValue(account.business_profile.services)}
              />
            </AttributesContainer>
          </div>
        )}

        {account_integrations.length > 0 && (
          <AccountIntegrationsDisplay integrations={account_integrations} />
        )}

        {standard_agents && standard_agents.length > 0 && (
          <AccountTrialTable
            accountUid={account.uid}
            agents={standard_agents}
          />
        )}

        <div className="space-y-3">
          <h2 className="text-base font-semibold">Landing page activity</h2>
          <AttributesContainer className="gap-6">
            <AttributeDisplay
              label="Visits"
              value={String(landing_page_analytics.visit_count)}
            />
            {Object.entries(CTA_LABELS).map(([key, label]) => (
              <AttributeDisplay
                key={key}
                label={`${label} clicks`}
                value={String(landing_page_analytics.cta_counts[key] ?? 0)}
              />
            ))}
          </AttributesContainer>

          {landing_page_analytics.recent_visits.length > 0 && (
            <div className="overflow-hidden rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-muted-foreground text-left text-xs">
                  <tr>
                    <th className="px-3 py-2 font-medium">When</th>
                    <th className="px-3 py-2 font-medium">Location</th>
                    <th className="px-3 py-2 font-medium">IP</th>
                    <th className="px-3 py-2 font-medium">CTA clicks</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {landing_page_analytics.recent_visits.map((v) => (
                    <tr key={v.id}>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {new Date(v.created_at).toLocaleString()}
                      </td>
                      <td className="px-3 py-2">{v.location || "—"}</td>
                      <td className="text-muted-foreground px-3 py-2">
                        {v.ip_address ?? "—"}
                      </td>
                      <td className="px-3 py-2">
                        {Object.keys(v.ctas).length === 0
                          ? "—"
                          : Object.entries(v.ctas)
                              .map(([cta, n]) =>
                                n > 1
                                  ? `${CTA_LABELS[cta] ?? cta} ×${n}`
                                  : (CTA_LABELS[cta] ?? cta),
                              )
                              .join(", ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  )
}
